import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as R from 'remeda';
import {
  batchDatapointConfirm,
  batchDatapointDelete,
  deleteDatapoint,
  materializeVirtualDatapoints,
  nextDatapoint,
  updateDatapointValue,
} from '../../../../../redux/modules/datapoints/actions';
import {
  currentMultivalueDatapointSelector,
  datapointsSelector,
} from '../../../../../redux/modules/datapoints/selector';
import { suggestedOperationsSelector } from '../../../../../redux/modules/datapoints/suggestedOperations/selector';
import { isSimpleDatapoint } from '../../../../../redux/modules/datapoints/typedHelpers';
import { SimpleDatapointDataST } from '../../../../../types/datapoints';
import { State } from '../../../../../types/state';
import { useCanvasSelectionActions } from '../../../document-store/DocumentStore';
import { createMaterializePayload } from './createMaterializePayload';
import { getTuplesFromSelectedDatapoints } from './getTuplesFromSelectedDatapoints';

export const useValidationDialogCallbacks = () => {
  // Suggested operations
  const suggestedOperations = useSelector(suggestedOperationsSelector);
  const { clearSelectedBboxes } = useCanvasSelectionActions();
  const pendingMaterialization = useSelector(
    (state: State) => state.datapoints.pendingMaterialization
  );

  const allDatapoints = useSelector(datapointsSelector);
  const currentMultivalueDatapoint = useSelector(
    currentMultivalueDatapointSelector
  );

  const dispatch = useDispatch();
  const handleDatapointValueChange = useCallback(
    (
      datapointId: number,
      datapointIndex: number,
      value: string,
      oldValue: string
    ) => {
      dispatch(
        updateDatapointValue(
          {
            id: datapointId,
            index: datapointIndex,
            oldValue,
            reason: 'input-edit-box',
          },
          value
        )
      );
    },
    [dispatch]
  );

  const handleTupleDelete = useCallback(
    (selectedDatapoints: SimpleDatapointDataST[]) => {
      const tuplesToDelete = getTuplesFromSelectedDatapoints(
        allDatapoints,
        selectedDatapoints
      );
      const payload = {
        datapointsToReset: [],
        tuplesToDelete,
      };

      dispatch(batchDatapointDelete(payload));
      clearSelectedBboxes();
    },
    [allDatapoints, clearSelectedBboxes, dispatch]
  );

  /**
   * This fn should always handle the following
   * 1. Delete at least one multivalue child / tuple using the validation dialog
   * 2. Delete at least one multivalue child / tuple along with other bounding boxes selected. (multivalues will be deleted, the others will be reset)
   * 3. Reset a simple datapoint
   */
  const handleDatapointsDelete = useCallback(
    (selectedDatapoints: SimpleDatapointDataST[]) => {
      const multivalueMap = R.mapToObj(allDatapoints, dp => [
        dp.id,
        dp.category === 'multivalue',
      ]);

      const [multivalueChildren, simpleDatapoints] = R.partition(
        selectedDatapoints,
        dp => Boolean(dp.meta.parentId && multivalueMap[dp.meta.parentId])
      );

      const payload = {
        datapointsToReset: simpleDatapoints.map(dp => ({
          id: dp.id,
          index: dp.meta.index,
        })),
        tuplesToDelete: [],
      };

      const multivalueChildrenToDelete = multivalueChildren.flatMap(dp =>
        dp.meta.parentId
          ? {
              id: dp.id,
              parentId: dp.meta.parentId,
            }
          : []
      );

      dispatch(batchDatapointDelete(payload));

      if (multivalueChildrenToDelete.length > 0) {
        // in thoery we can refactor a few things in batchDatapointDlete and do this in one go
        // But the complexity it adds to the epic/reducer structure defeats the point
        multivalueChildrenToDelete.forEach(dp =>
          dispatch(deleteDatapoint([-1, dp.parentId, dp.id]))
        );
      }

      clearSelectedBboxes();
    },
    [allDatapoints, clearSelectedBboxes, dispatch]
  );

  const handleDatapointConfirm = useCallback(
    (selectedDatapoints: SimpleDatapointDataST[], next?: boolean) => {
      const replaceOperations = Object.values(suggestedOperations).filter(
        op => op.source === 'table' && op.op === 'replace'
      );

      const materializePayload = currentMultivalueDatapoint
        ? createMaterializePayload(
            allDatapoints,
            replaceOperations,
            selectedDatapoints,
            currentMultivalueDatapoint
          )
        : undefined;

      // On confirm, materialize selected suggestion boxes if you are in a multivalue.
      // Do not materialize if another materialization is in progress - risk of race condition.
      if (
        materializePayload &&
        (materializePayload.tuplesToCreate.length ||
          materializePayload.datapointsToReplace.length) &&
        !pendingMaterialization
      ) {
        dispatch(materializeVirtualDatapoints(materializePayload));
      }

      if (!next) {
        const datapointsToReplace =
          materializePayload?.datapointsToReplace ?? [];
        const tuplesToCreate = materializePayload?.tuplesToCreate ?? [];
        const affectedIds: number[] = [
          ...datapointsToReplace.map(dp => dp.datapointId),
          ...tuplesToCreate.flatMap(tuple => tuple.validatedDatapointIds),
        ];

        // Subtract datapoints created during materialization step.
        const regularDatapoints = selectedDatapoints.filter(
          dp => !affectedIds.includes(dp.id)
        );

        const datapoints = regularDatapoints
          .filter(isSimpleDatapoint)
          .filter(dp => !dp.schema?.hidden);

        dispatch(batchDatapointConfirm({ datapoints }));
      } else {
        dispatch(nextDatapoint());
      }
      clearSelectedBboxes();
    },
    [
      allDatapoints,
      clearSelectedBboxes,
      currentMultivalueDatapoint,
      dispatch,
      pendingMaterialization,
      suggestedOperations,
    ]
  );

  return {
    handleDatapointValueChange,
    handleDatapointConfirm,
    handleTupleDelete,
    handleDatapointsDelete,
  };
};
